import React, { useState, useEffect, useCallback } from 'react';
import {
    X, TrendingUp, RefreshCw, Download, BarChart3,
    Users, Clock, Shield, CheckCircle, Calendar,
    ArrowUpRight, ArrowDownRight, Minus, Camera,
    Maximize2, Minimize2
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
    StatCard,
    LineChart,
    BarChart,
    ExpandableVisualization
} from '../Analytics';
import useDrawerResize from './useDrawerResize';
import AnalyticsAIPanel from './AnalyticsAIPanel';
import NotificationSettingsPanel from './NotificationSettingsPanel';

const PERIODS = [
    { value: '7', label: '7 Days' },
    { value: '30', label: '30 Days' },
    { value: '90', label: '90 Days' },
    { value: '180', label: '6 Months' },
    { value: '365', label: '1 Year' },
];

const TrendsAnalyticsDrawer = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [trendsData, setTrendsData] = useState(null);
    const [snapshots, setSnapshots] = useState(null);
    const [comparison, setComparison] = useState(null);
    const [period, setPeriod] = useState('30');
    const [snapshotLoading, setSnapshotLoading] = useState(false);

    const { drawerWidth, isDragging, dragHandleProps, dragHandleElement, isFullscreen, toggleFullscreen } = useDrawerResize({
        minWidth: 500, maxWidth: 950, defaultWidth: 720,
        storageKey: 'trendsAnalyticsDrawerWidth',
    });

    const fetchTrends = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [trendsRes, snapshotsRes] = await Promise.all([
                api.get(`/analytics/trends?period=${period}`, { headers }),
                api.get('/analytics/snapshots?months=6', { headers }).catch(() => ({ data: { snapshots: [], comparison: null } }))
            ]);

            setTrendsData(trendsRes.data.trends);
            setSnapshots(snapshotsRes.data.snapshots || []);
            setComparison(snapshotsRes.data.comparison || null);
        } catch (error) {
            console.error('Error fetching trends:', error);
            toast.error('Failed to load trend analytics');
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        if (isOpen) {
            fetchTrends();
            document.body.style.overflow = 'hidden';
        } else {
            setTrendsData(null);
            setSnapshots(null);
            setComparison(null);
            document.body.style.overflow = 'unset';
            setLoading(true);
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen, fetchTrends]);

    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    const handleDownloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/analytics/report', {
                reportType: 'comprehensive', format: 'csv'
            }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'trends-analytics.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Trends exported');
        } catch { toast.error('Export failed'); }
    };

    const handleCreateSnapshot = async () => {
        setSnapshotLoading(true);
        try {
            const token = localStorage.getItem('token');
            await api.post('/analytics/snapshots', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Snapshot saved');
            fetchTrends(); // Refresh to show latest
        } catch {
            toast.error('Failed to create snapshot');
        } finally {
            setSnapshotLoading(false);
        }
    };

    const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const formatMonth = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity" onClick={onClose} />
            <div
                className="relative h-full bg-[#FAFAF9] shadow-2xl flex flex-col animate-slide-in-right"
                style={{
                    width: isFullscreen ? '100vw' : `${drawerWidth}px`,
                    maxWidth: '100vw',
                    transition: isDragging ? 'none' : 'width 0.3s ease',
                }}
            >
                {/* ── Drag handle (left edge) — hidden in fullscreen ── */}
                {!isFullscreen && (
                    <div {...dragHandleProps}>
                        <div {...dragHandleElement} />
                    </div>
                )}

                {/* Header */}
                <div className="px-6 py-4 bg-white border-b border-[#E5E1DD] flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-50 rounded-lg">
                            <TrendingUp className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Trends & History</h2>
                            <p className="text-sm text-gray-500">Time-based analytics & comparisons</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownloadCSV}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Download CSV"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={fetchTrends}
                            disabled={loading}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Refresh"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={toggleFullscreen}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                        </button>
                        <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Period Selector */}
                <div className="px-6 py-3 bg-white border-b border-[#E5E1DD] flex items-center gap-2 flex-shrink-0">
                    {PERIODS.map(p => (
                        <button
                            key={p.value}
                            onClick={() => setPeriod(p.value)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${period === p.value
                                ? 'bg-emerald-100 text-emerald-700 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 scrollbar-hide min-w-0">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64 space-y-4">
                            <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
                            <p className="text-gray-500 font-medium">Loading trends...</p>
                        </div>
                    ) : trendsData ? (
                        <div className="space-y-6">

                            {/* ── Task Completions Over Time ── */}
                            <ExpandableVisualization
                                title="Task Completions Over Time"
                                data={trendsData.taskCompletions?.map(d => ({
                                    date: formatDate(d.date),
                                    completions: d.completions,
                                    avgMinutes: d.avgTimeMinutes
                                })) || []}
                                columns={[
                                    { key: 'date', label: 'Date' },
                                    { key: 'completions', label: 'Completions' },
                                    { key: 'avgMinutes', label: 'Avg Time (min)' }
                                ]}
                                exportFilename="task-completions-trend"
                            >
                                <div className="bg-white rounded-xl border border-gray-200 p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <BarChart3 className="w-4 h-4 text-blue-500" />
                                        <h3 className="font-semibold text-gray-800">Task Completions</h3>
                                    </div>
                                    {trendsData.taskCompletions?.length > 0 ? (
                                        <LineChart
                                            data={trendsData.taskCompletions.map(d => ({
                                                date: formatDate(d.date),
                                                value: d.completions
                                            }))}
                                            height={200}
                                            lineColor="#3B82F6"
                                        />
                                    ) : (
                                        <p className="text-gray-400 text-sm text-center py-8">No data for this period</p>
                                    )}
                                </div>
                            </ExpandableVisualization>

                            {/* ── Quiz Scores Over Time ── */}
                            <ExpandableVisualization
                                title="Quiz Scores Over Time"
                                data={trendsData.quizScores?.map(d => ({
                                    date: formatDate(d.date),
                                    avgScore: d.avgScore,
                                    attempts: d.attempts
                                })) || []}
                                columns={[
                                    { key: 'date', label: 'Date' },
                                    { key: 'avgScore', label: 'Avg Score %' },
                                    { key: 'attempts', label: 'Attempts' }
                                ]}
                                exportFilename="quiz-scores-trend"
                            >
                                <div className="bg-white rounded-xl border border-gray-200 p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <CheckCircle className="w-4 h-4 text-blue-500" />
                                        <h3 className="font-semibold text-gray-800">Average Quiz Scores</h3>
                                    </div>
                                    {trendsData.quizScores?.length > 0 ? (
                                        <LineChart
                                            data={trendsData.quizScores.map(d => ({
                                                date: formatDate(d.date),
                                                value: d.avgScore
                                            }))}
                                            height={200}
                                            lineColor="#60A5FA"
                                        />
                                    ) : (
                                        <p className="text-gray-400 text-sm text-center py-8">No quiz data for this period</p>
                                    )}
                                </div>
                            </ExpandableVisualization>

                            {/* ── New Employees & Survey Completions ── */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <ExpandableVisualization
                                    title="New Employees"
                                    data={trendsData.newEmployees?.map(d => ({ date: formatDate(d.date), count: d.count })) || []}
                                    columns={[{ key: 'date', label: 'Date' }, { key: 'count', label: 'Count' }]}
                                    exportFilename="new-employees-trend"
                                >
                                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <Users className="w-4 h-4 text-blue-500" />
                                            <h3 className="font-semibold text-gray-800 text-sm">New Employees</h3>
                                        </div>
                                        {trendsData.newEmployees?.length > 0 ? (
                                            <LineChart
                                                data={trendsData.newEmployees.map(d => ({
                                                    date: formatDate(d.date),
                                                    value: d.count
                                                }))}
                                                height={160}
                                                lineColor="#2563EB"
                                            />
                                        ) : (
                                            <p className="text-gray-400 text-sm text-center py-6">No data</p>
                                        )}
                                    </div>
                                </ExpandableVisualization>

                                <ExpandableVisualization
                                    title="Survey Completions"
                                    data={trendsData.surveyCompletions?.map(d => ({ date: formatDate(d.date), completions: d.completions })) || []}
                                    columns={[{ key: 'date', label: 'Date' }, { key: 'completions', label: 'Completions' }]}
                                    exportFilename="survey-completions-trend"
                                >
                                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <CheckCircle className="w-4 h-4 text-blue-500" />
                                            <h3 className="font-semibold text-gray-800 text-sm">Survey Completions</h3>
                                        </div>
                                        {trendsData.surveyCompletions?.length > 0 ? (
                                            <LineChart
                                                data={trendsData.surveyCompletions.map(d => ({
                                                    date: formatDate(d.date),
                                                    value: d.completions
                                                }))}
                                                height={160}
                                                lineColor="#93C5FD"
                                            />
                                        ) : (
                                            <p className="text-gray-400 text-sm text-center py-6">No data</p>
                                        )}
                                    </div>
                                </ExpandableVisualization>
                            </div>

                            {/* ── Compliance Rate Over Time ── */}
                            <ExpandableVisualization
                                title="Compliance Rate Trend"
                                data={trendsData.complianceRate?.map(d => ({ date: formatDate(d.date), rate: d.rate })) || []}
                                columns={[{ key: 'date', label: 'Date' }, { key: 'rate', label: 'Rate %' }]}
                                exportFilename="compliance-rate-trend"
                            >
                                <div className="bg-white rounded-xl border border-gray-200 p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Shield className="w-4 h-4 text-blue-500" />
                                        <h3 className="font-semibold text-gray-800">Compliance Completion Rate</h3>
                                    </div>
                                    {trendsData.complianceRate?.length > 0 ? (
                                        <LineChart
                                            data={trendsData.complianceRate.map(d => ({
                                                date: formatDate(d.date),
                                                value: d.rate
                                            }))}
                                            height={200}
                                            lineColor="#60A5FA"
                                        />
                                    ) : (
                                        <p className="text-gray-400 text-sm text-center py-8">No compliance data</p>
                                    )}
                                </div>
                            </ExpandableVisualization>

                            {/* ── Onboarding Completion Rate ── */}
                            {trendsData.onboardingCompletionRate?.length > 0 && (
                                <ExpandableVisualization
                                    title="Onboarding Completion Rate Trend"
                                    data={trendsData.onboardingCompletionRate.map(d => ({ date: formatDate(d.date), rate: d.rate, tasks: d.total }))}
                                    columns={[{ key: 'date', label: 'Date' }, { key: 'rate', label: 'Rate %' }, { key: 'tasks', label: 'Tasks' }]}
                                    exportFilename="onboarding-completion-trend"
                                >
                                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <TrendingUp className="w-4 h-4 text-blue-500" />
                                            <h3 className="font-semibold text-gray-800">Onboarding Completion Rate</h3>
                                        </div>
                                        <LineChart
                                            data={trendsData.onboardingCompletionRate.map(d => ({
                                                date: formatDate(d.date),
                                                value: d.rate
                                            }))}
                                            height={200}
                                            lineColor="#3B82F6"
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* ── Lifecycle Duration Funnel ── */}
                            {trendsData.lifecycleDurations?.length > 0 && (
                                <div className="bg-white rounded-xl border border-gray-200 p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Clock className="w-4 h-4 text-blue-500" />
                                        <h3 className="font-semibold text-gray-800">Lifecycle Duration by Month</h3>
                                        <span className="text-xs text-gray-400 ml-auto">Avg days per stage</span>
                                    </div>
                                    <div className="space-y-3">
                                        {trendsData.lifecycleDurations.map((item, i) => (
                                            <div key={i} className="flex items-center gap-4 py-2 border-b border-gray-50 last:border-0">
                                                <span className="text-sm font-medium text-gray-600 w-20">{formatMonth(item.month)}</span>
                                                <div className="flex-1 flex items-center gap-3">
                                                    <div className="flex items-center gap-2 flex-1">
                                                        <div className="h-6 bg-amber-100 rounded" style={{ width: `${Math.min((item.avgPreboardingDays || 0) * 3, 100)}%`, minWidth: item.avgPreboardingDays ? '24px' : '0' }}>
                                                            <span className="px-2 text-xs font-medium text-blue-800 leading-6 whitespace-nowrap">
                                                                {item.avgPreboardingDays ?? '—'}d pre
                                                            </span>
                                                        </div>
                                                        <div className="h-6 bg-blue-100 rounded" style={{ width: `${Math.min((item.avgOnboardingDays || 0) * 3, 100)}%`, minWidth: item.avgOnboardingDays ? '24px' : '0' }}>
                                                            <span className="px-2 text-xs font-medium text-blue-800 leading-6 whitespace-nowrap">
                                                                {item.avgOnboardingDays ?? '—'}d onb
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <span className="text-xs text-gray-400 w-16 text-right">{item.employeeCount} emp</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100">
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-3 h-3 bg-amber-100 rounded" />
                                            <span className="text-xs text-gray-500">Preboarding</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-3 h-3 bg-blue-100 rounded" />
                                            <span className="text-xs text-gray-500">Onboarding</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── Month-over-Month Snapshot Comparison ── */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-blue-500" />
                                        <h3 className="font-semibold text-gray-800">Monthly Snapshots</h3>
                                    </div>
                                    <button
                                        onClick={handleCreateSnapshot}
                                        disabled={snapshotLoading}
                                        className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                                    >
                                        <Camera className="w-3.5 h-3.5" />
                                        {snapshotLoading ? 'Saving...' : 'Take Snapshot'}
                                    </button>
                                </div>

                                {comparison ? (
                                    <div className="space-y-3">
                                        <p className="text-xs text-gray-500 mb-3">
                                            Comparing current vs previous snapshot
                                        </p>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            <ComparisonCard
                                                label="Task Completion"
                                                delta={comparison.changes.tasks.completionRate}
                                                suffix="%"
                                            />
                                            <ComparisonCard
                                                label="Overdue Tasks"
                                                delta={comparison.changes.tasks.overdue}
                                                invert
                                            />
                                            <ComparisonCard
                                                label="Avg Quiz Score"
                                                delta={comparison.changes.quizzes.avgScore}
                                                suffix="%"
                                            />
                                            <ComparisonCard
                                                label="Quiz Pass Rate"
                                                delta={comparison.changes.quizzes.passRate}
                                                suffix="%"
                                            />
                                            <ComparisonCard
                                                label="Survey Response"
                                                delta={comparison.changes.surveys.responseRate}
                                                suffix="%"
                                            />
                                            <ComparisonCard
                                                label="Compliance"
                                                delta={comparison.changes.compliance.completionRate}
                                                suffix="%"
                                            />
                                        </div>
                                    </div>
                                ) : snapshots && snapshots.length > 0 ? (
                                    <div>
                                        <p className="text-sm text-gray-500 mb-3">Latest snapshot available. Take another to see comparisons.</p>
                                        <SnapshotSummary data={snapshots[0].data} />
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <Camera className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                                        <p className="text-sm text-gray-500">No snapshots yet</p>
                                        <p className="text-xs text-gray-400 mt-1">
                                            Take a snapshot to start tracking month-over-month changes
                                        </p>
                                    </div>
                                )}

                                {/* Historical snapshot bars */}
                                {snapshots && snapshots.length > 1 && (
                                    <div className="mt-5 pt-4 border-t border-gray-100">
                                        <h4 className="text-sm font-medium text-gray-700 mb-3">Completion Rate History</h4>
                                        <BarChart
                                            data={[...snapshots].reverse().map(s => ({
                                                label: formatMonth(s.periodStart),
                                                value: s.data?.tasks?.completionRate || 0,
                                                suffix: '%'
                                            }))}
                                            horizontal
                                            barColor="#3B82F6"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* AI Chat Panel */}
                            <AnalyticsAIPanel
                                analyticsContext={{
                                    type: 'trends',
                                    title: 'Trends & Historical Analytics',
                                    data: trendsData,
                                }}
                            />

                            {/* Notification Settings */}
                            <NotificationSettingsPanel
                                analyticsContext={{
                                    type: 'trends',
                                    title: 'Trends & Historical Analytics',
                                    data: trendsData,
                                }}
                            />
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <TrendingUp className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                            <p className="font-medium">No trend data available</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ─── Comparison Card ──────────────────────────────────────────────── */
const ComparisonCard = ({ label, delta, suffix = '', invert = false }) => {
    const isPositive = invert ? delta < 0 : delta > 0;
    const isNegative = invert ? delta > 0 : delta < 0;
    const isNeutral = delta === 0;

    return (
        <div className={`rounded-lg p-3 border ${isPositive ? 'bg-green-50 border-green-200' :
            isNegative ? 'bg-red-50 border-red-200' :
                'bg-gray-50 border-gray-200'
            }`}>
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <div className="flex items-center gap-1">
                {isPositive && <ArrowUpRight className="w-4 h-4 text-blue-600" />}
                {isNegative && <ArrowDownRight className="w-4 h-4 text-red-600" />}
                {isNeutral && <Minus className="w-4 h-4 text-gray-400" />}
                <span className={`text-lg font-bold ${isPositive ? 'text-blue-700' :
                    isNegative ? 'text-red-700' :
                        'text-gray-600'
                    }`}>
                    {delta > 0 ? '+' : ''}{delta}{suffix}
                </span>
            </div>
        </div>
    );
};

/* ─── Snapshot Summary ─────────────────────────────────────────────── */
const SnapshotSummary = ({ data }) => {
    if (!data) return null;
    return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-gray-900">{data.employees?.total || 0}</p>
                <p className="text-xs text-gray-500">Employees</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-gray-900">{data.tasks?.completionRate || 0}%</p>
                <p className="text-xs text-gray-500">Completion Rate</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-gray-900">{data.quizzes?.avgScore || 0}%</p>
                <p className="text-xs text-gray-500">Avg Quiz Score</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-gray-900">{data.compliance?.completionRate || 0}%</p>
                <p className="text-xs text-gray-500">Compliance</p>
            </div>
        </div>
    );
};

export default TrendsAnalyticsDrawer;
